import React from "react";
import { useAppSelector } from "../../../app/hooks";
import {
  selectActiveColleagues,
  selectColleagues,
} from "../instructors-slice";
import { instructorAccentClass } from "../utils/instructorAccent";

interface InstructorNameTagProps {
  instructorId?: string | null;
  /** Show a muted "Unassigned" label when there is no instructor (members list). */
  showUnassigned?: boolean;
  className?: string;
}

/**
 * Small "who owns this" label for list rows: the instructor's calendar accent
 * dot plus their name. Renders nothing in a solo organization. Purely
 * presentational — the page is responsible for loading the roster (see
 * useColleagues), since list rows mount many times at once.
 */
const InstructorNameTag: React.FC<InstructorNameTagProps> = ({
  instructorId,
  showUnassigned = false,
  className = "",
}) => {
  const colleagues = useAppSelector(selectColleagues);
  const activeColleagues = useAppSelector(selectActiveColleagues);

  if (activeColleagues.length <= 1) {
    return null;
  }

  if (!instructorId) {
    if (!showUnassigned) {
      return null;
    }
    return (
      <p className={`text-xs text-grey-50 dark:text-grey-70 ${className}`.trim()}>
        Unassigned
      </p>
    );
  }

  // Resolve against the full roster — a session or member may still belong to
  // a since-deactivated colleague.
  const instructor = colleagues.find((c) => c.id === instructorId);
  if (!instructor) {
    return null;
  }

  return (
    <p
      className={`flex items-center gap-1.5 text-xs text-grey-40 dark:text-grey-60 ${className}`.trim()}
    >
      <span
        aria-hidden="true"
        className={`inline-block h-2 w-2 shrink-0 rounded-full ${instructorAccentClass(instructor.id)}`}
      />
      <span>{instructor.fullName}</span>
    </p>
  );
};

export default InstructorNameTag;
